// WHAT: Custom hook for managing Yazio settings state and operations
// WHY:  Centralizes Yazio settings logic to reduce component complexity
// DOMAIN: Settings → Yazio
// DEPENDENCIES: @tauri-apps/api/core, React hooks
// RISKS:
//   - WARN: Credentials are stored in Tauri secure storage; backend commands are critical.

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";

/**
 * WHAT:
 *   Represents the current Yazio settings state.
 *
 * WHY:
 *   To type-check the settings object returned from the backend.
 */
export interface YazioSettings {
  hasClientCredentials?: boolean;
  hasCredentials: boolean;
  username?: string;
}

/**
 * WHAT:
 *   Custom hook that manages Yazio settings state and operations.
 *
 * WHY:
 *   Centralizes all Yazio settings logic to reduce component complexity.
 *
 * BEHAVIOR:
 *   - Loads settings on mount
 *   - Provides handlers for save, edit, remove, and test operations
 *   - Manages form state and UI state (loading, editing, etc.)
 *
 * @returns {object} Yazio settings state and handlers
 *
 */
export const useYazioSettings = () => {
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isEditingClientCredentials, setIsEditingClientCredentials] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState<undefined | YazioSettings>();
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState<string | undefined>();
  const [testResult, setTestResult] = useState<"error" | "success" | undefined>();

  // Form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  /**
   * WHAT:
   *   Loads the current Yazio settings from the Tauri backend.
   */
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const loadedSettings = await core.invoke<YazioSettings>("get_yazio_settings");
      setSettings(loadedSettings);
      setIsConfigured(loadedSettings.hasCredentials);
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "Failed to load settings";
      setError(message);
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  /**
   * WHAT:
   *   Saves the entered Yazio credentials to secure storage.
   */
  const handleSave = useCallback(async () => {
    if (!username.trim() || !password.trim()) {
      setError("Username and password are required.");
      return;
    }

    setSaving(true);
    setError(undefined);
    setSuccess(undefined);

    try {
      await core.invoke("store_yazio_credentials", {
        password: password.trim(),
        username: username.trim(),
      });

      const updatedSettings = await core.invoke<YazioSettings>("get_yazio_settings");
      setSettings(updatedSettings);
      setIsConfigured(true);
      setIsEditing(false);
      setSuccess("✅ Yazio credentials saved successfully!");

      setUsername("");
      setPassword("");
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Error saving credentials.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [username, password]);

  /**
   * WHAT:
   *   Saves the entered OAuth Client Credentials to secure storage.
   */
  const handleSaveClientCredentials = useCallback(async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setError("Client ID and Client Secret are required.");
      return;
    }

    setSaving(true);
    setError(undefined);
    setSuccess(undefined);

    try {
      await core.invoke("store_yazio_client_credentials", {
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      });

      const updatedSettings = await core.invoke<YazioSettings>("get_yazio_settings");
      setSettings(updatedSettings);
      setIsEditingClientCredentials(false);
      setSuccess("✅ OAuth Client Credentials saved successfully!");

      setClientId("");
      setClientSecret("");
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "❌ Error saving client credentials.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [clientId, clientSecret]);

  /**
   * WHAT:
   *   Removes stored Yazio credentials from secure storage.
   */
  const handleRemove = useCallback(async () => {
    try {
      setSaving(true);
      setError(undefined);
      setSuccess(undefined);

      await core.invoke("clear_yazio_credentials");
      const updatedSettings = await core.invoke<YazioSettings>("get_yazio_settings");
      setSettings(updatedSettings);
      setIsConfigured(false);
      setIsEditing(false);
      setSuccess("✅ Yazio credentials removed successfully!");
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Error removing credentials.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }, []);

  /**
   * WHAT:
   *   Removes stored OAuth Client Credentials from secure storage.
   */
  const handleRemoveClientCredentials = useCallback(async () => {
    try {
      setSaving(true);
      setError(undefined);
      setSuccess(undefined);

      await core.invoke("clear_yazio_client_credentials");
      const updatedSettings = await core.invoke<YazioSettings>("get_yazio_settings");
      setSettings(updatedSettings);
      setIsEditingClientCredentials(false);
      setSuccess("✅ OAuth Client Credentials removed successfully!");
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "❌ Error removing client credentials.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }, []);

  /**
   * WHAT:
   *   Tests the connection to Yazio API using stored credentials.
   */
  const handleTestConnection = useCallback(async () => {
    try {
      setTesting(true);
      setError(undefined);
      setTestResult(undefined);

      const result = await core.invoke<{ success: boolean; message?: string }>(
        "test_yazio_connection",
      );

      if (result.success) {
        setTestResult("success");
        setSuccess("✅ Connection successful! Yazio credentials are valid.");
      } else {
        setTestResult("error");
        setError(`❌ Connection failed: ${result.message ?? "Please check your credentials."}`);
      }
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "❌ Connection test failed.";
      setTestResult("error");
      const fallbackMessage = "Please check your credentials.";
      setError(`❌ Connection failed: ${message || fallbackMessage}`);
    } finally {
      setTesting(false);
    }
  }, []);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setError(undefined);
    setSuccess(undefined);
    setTestResult(undefined);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setUsername("");
    setPassword("");
    setError(undefined);
    setSuccess(undefined);
    setTestResult(undefined);
  }, []);

  const handleEditClientCredentials = useCallback(() => {
    setIsEditingClientCredentials(true);
    setError(undefined);
    setSuccess(undefined);
  }, []);

  const handleCancelClientCredentials = useCallback(() => {
    setIsEditingClientCredentials(false);
    setClientId("");
    setClientSecret("");
    setError(undefined);
    setSuccess(undefined);
  }, []);

  return {
    // State
    clientId,
    clientSecret,
    error,
    isConfigured,
    isEditing,
    isEditingClientCredentials,
    loading,
    password,
    saving,
    settings,
    success,
    testing,
    testResult,
    username,

    // Handlers
    handleCancel,
    handleCancelClientCredentials,
    handleEdit,
    handleEditClientCredentials,
    handleRemove,
    handleRemoveClientCredentials,
    handleSave,
    handleSaveClientCredentials,
    handleTestConnection,
    setClientId,
    setClientSecret,
    setPassword,
    setUsername,
  };
};
